import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import POSPage from './pages/POSPage';
import InventoryPage from './pages/InventoryPage';
import UsersPage from './pages/UsersPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import BranchesPage from './pages/BranchesPage';

// Layout
import Layout from './components/Layout/Layout';
import { SyncManager } from './utils/SyncManager';

// Protected Route Component
const ProtectedRoute = ({ children, requiredPermission }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="loading-container" style={{ height: '100vh' }}>
                <div className="spinner spinner-lg"></div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Check permission if required
    if (requiredPermission && user.role !== 'super_admin' && user.role !== 'branch_admin') {
        const hasPermission = user.permissions?.[requiredPermission]?.view;
        if (!hasPermission) {
            return <Navigate to="/pos" replace />;
        }
    }

    return children;
};

// Super Admin Only Route
const SuperAdminRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="loading-container" style={{ height: '100vh' }}>
                <div className="spinner spinner-lg"></div>
            </div>
        );
    }

    if (!user || user.role !== 'super_admin') {
        return <Navigate to="/pos" replace />;
    }

    return children;
};

function App() {
    const { user } = useAuth();

    // Attempt to sync offline orders on app load
    React.useEffect(() => {
        const sync = async () => {
            if (navigator.onLine && SyncManager && typeof SyncManager.syncOrders === 'function') {
                try {
                    console.log('App loaded online. Attempting to sync...');
                    await SyncManager.syncOrders();
                } catch (error) {
                    console.error('Auto-sync failed:', error);
                }
            }
        };
        sync();
    }, []);

    return (
        <Routes>
            {/* Public Routes */}
            <Route
                path="/login"
                element={user ? <Navigate to="/pos" replace /> : <LoginPage />}
            />
            <Route
                path="/register"
                element={user ? <Navigate to="/pos" replace /> : <RegisterPage />}
            />

            {/* Protected Routes with Layout */}
            <Route
                path="/"
                element={
                    <ProtectedRoute>
                        <Layout />
                    </ProtectedRoute>
                }
            >
                <Route index element={<Navigate to="/pos" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route
                    path="pos"
                    element={
                        <ProtectedRoute requiredPermission="pos">
                            <POSPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="inventory"
                    element={
                        <ProtectedRoute requiredPermission="inventory">
                            <InventoryPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="users"
                    element={
                        <ProtectedRoute requiredPermission="users">
                            <UsersPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="reports"
                    element={
                        <ProtectedRoute requiredPermission="reports">
                            <ReportsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="settings"
                    element={
                        <ProtectedRoute requiredPermission="settings">
                            <SettingsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="branches"
                    element={
                        <SuperAdminRoute>
                            <BranchesPage />
                        </SuperAdminRoute>
                    }
                />
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/pos" replace />} />
        </Routes>
    );
}

export default App;
